import { BaseView } from '@src/common/components/views/BaseView.tsx';
import { DirectusContextWrapper } from '@components/directusTable/directusContext.tsx';
import {
  Navigate,
  Outlet,
  type RouteObject,
  createBrowserRouter,
} from 'react-router-dom';
import { Paths } from '@src/config/paths.ts';
import { UserContextWrapper } from '../contexts/userContext.tsx';
import { chatbotBuilderViewLoader } from '../features/chatbot/views/ChatBotBuilderView.loader.ts';
import { chatbotViewLoader } from '../features/chatbot/views/ChatBotView.loader.ts';
import { editJobWizardViewLoader } from '@src/features/jobs/views/createJobWizardView.loader.ts';
import { jobsDetailsViewLoader } from '@src/features/jobs/views/JobsDetailsView.loader.ts';
import { loginLoader } from '../features/auth/views/LoginView.functions.ts';
import {
  logoutAction,
  logoutLoader,
} from '../features/auth/views/LogoutView.functions.ts';
import { organizationDetailsViewLoader } from '@src/features/organizations/views/OrganizationDetailsView.loader.ts';
import { organizationsListViewLoader } from '@src/features/organizations/views/OrganizationsListView.functions.ts';
import { profileViewLoader } from '@src/features/user/views/ProfileView.loader.ts';
import { registerLoader } from '@src/features/auth/views/RegisterView.loader.ts';
import { rootLoader } from '@src/common/components/views/Root.loader.ts';
import GlobalErrorHandler from '../common/components/views/global-error-handler.tsx';
import JobWizardView from '@src/features/jobs/views/JobWizardView.tsx';
import JobsView from '@src/features/jobs/views/JobsView.tsx';
import OrganizationDetailsView from '@src/features/organizations/views/OrganizationDetailsView.tsx';
import OrganizationInviteAcceptView from '@src/features/organizations/views/OrganizationInviteAcceptView.tsx';
import OrganizationsListView from '@src/features/organizations/views/OrganizationsListView.tsx';
import Root from '../common/components/views/Root.tsx';
import { GTMWrapper } from '../contexts/GTMWrapper.tsx';
import { creativeBuilderForJobViewLoader } from '../features/creativeBuilder/views/CreativeBuilderForJobView.loader.ts';

export const routes: RouteObject[] = [
  {
    errorElement: <GlobalErrorHandler />,
    children: [
      {
        id: 'app',
        children: [
          {
            path: Paths.LOGIN,
            lazy: () =>
              import('../features/auth/views/LoginView.tsx').then((module) => ({
                element: <module.LoginView />,
                errorElement: <module.LoginView />,
              })),
            loader: loginLoader,
          },
          {
            path: Paths.LOGOUT,
            lazy: () => import('../features/auth/views/LogoutView.tsx'),
            loader: logoutLoader,
            action: logoutAction,
            errorElement: <GlobalErrorHandler />,
          },
          {
            path: Paths.REGISTER,
            loader: registerLoader,
            children: [
              {
                index: true,
                lazy: () => import('../features/auth/views/RegisterView.tsx'),
              },
              {
                path: Paths.VERIFY_MAIL.slice(1),
                lazy: () =>
                  import('../features/auth/views/VerifyEmailView.tsx'),
              },
            ],
          },
          {
            path: Paths.RESET,
            lazy: () => import('../features/user/views/ResetView.tsx'),
            errorElement: <GlobalErrorHandler />,
          },
          {
            path: Paths.START_PASSWORD_RESET,
            lazy: () =>
              import('../features/auth/views/StartPasswordResetView.tsx'),
            errorElement: <GlobalErrorHandler />,
          },
          {
            id: 'root',
            element: (
              <Root>
                <DirectusContextWrapper>
                  <Outlet />
                </DirectusContextWrapper>
              </Root>
            ),
            loader: rootLoader,
            shouldRevalidate: (params) => {
              // we don't need to revalidate everytime.
              // DefaultShouldRevalidate is only true if we enter the website the first time,
              // or after we call revalidator.revalidate
              if (!params.defaultShouldRevalidate) {
                return false;
              }
              // entering the wizard is considered a new page call,
              // so we need to prevent the revalidation or the wizard breaks
              // We otherwise want to allow to manual revalidate in case the user switches his settings
              return (
                !params.currentUrl.pathname.includes('/jobs') &&
                !params.currentUrl.pathname.includes('/wizard')
              );
            },
            children: [
              {
                index: true,
                element: <Navigate to={Paths.JOBS} replace={true} />,
              },
              {
                element: (
                  <BaseView>
                    <Outlet />
                  </BaseView>
                ),
                children: [
                  {
                    path: Paths.ORGANIZATIONS,
                    children: [
                      {
                        index: true,
                        element: <OrganizationsListView />,
                        loader: organizationsListViewLoader,
                      },
                      {
                        path: ':organizationId/accept',
                        element: <OrganizationInviteAcceptView />,
                      },
                      {
                        path: ':organizationId',
                        element: <OrganizationDetailsView />,
                        loader: organizationDetailsViewLoader,
                      },
                    ],
                  },
                  {
                    path: Paths.JOBS,
                    children: [
                      {
                        index: true,
                        element: <JobsView />,
                      },
                      {
                        path: Paths.JOB_DETAILS,
                        lazy: () =>
                          import('../features/jobs/views/JobDetailsView.tsx'),
                        loader: jobsDetailsViewLoader,
                      },
                      {
                        path: Paths.JOB_CREATIVE_BUILDER,
                        lazy: () =>
                          import(
                            '../features/creativeBuilder/views/CreativeBuilderForJobView.tsx'
                          ),
                        loader: creativeBuilderForJobViewLoader,
                      },
                    ],
                  },
                  {
                    path: Paths.PROFILE,
                    lazy: () =>
                      import('../features/user/views/ProfileView.tsx'),
                    loader: profileViewLoader,
                  },
                  {
                    path: Paths.SUPPORT,
                    lazy: () =>
                      import('../features/support/views/SupportView.tsx'),
                  },
                  {
                    path: Paths.COACHING,
                    lazy: () =>
                      import('../features/support/views/CoachingView.tsx'),
                  },
                  {
                    path: Paths.CREATIVE_BUILDER,
                    lazy: () =>
                      import(
                        '../features/creativeBuilder/views/CreativeBuilderView.tsx'
                      ),
                  },
                ],
              },
              // Routes without BaseView
              {
                path: Paths.JOB_WIZARD,
                element: (
                  <UserContextWrapper>
                    <JobWizardView />
                  </UserContextWrapper>
                ),
                loader: editJobWizardViewLoader,
                shouldRevalidate: ({ currentParams, nextParams }) => {
                  return currentParams.jobId !== nextParams.jobId;
                },
              },
              {
                path: Paths.CHATBOT_BUILDER,
                lazy: () =>
                  import('../features/chatbot/views/ChatbotBuilderView.tsx'),
                loader: chatbotBuilderViewLoader,
              },
            ],
          },
        ],
        element: (
          <GTMWrapper>
            <Outlet />
          </GTMWrapper>
        ),
      },
      {
        id: 'chatbot-view',
        path: Paths.CHATBOT,
        errorElement: <GlobalErrorHandler />,
        lazy: () => import('../features/chatbot/views/ChatBotView.tsx'),
        loader: chatbotViewLoader,
      },
    ],
  },
];

export const browserRouter = createBrowserRouter(routes);
